import { useEffect, useState } from 'react';

import { leaveCompany, type SupportVisit } from '../api.js';
import { VENDOR } from '../brand.js';

/**
 * The strip that says somebody from the vendor is in this company's data.
 *
 * IT IS DRAWN FOR BOTH SIDES, AND THAT IS THE FEATURE.
 *
 * For the CUSTOMER it is the whole point of the design. The user was offered
 * the quieter option — only the operator sees it, the customer finds it in
 * their audit trail afterwards — and chose this one: *"they see it live"*. A
 * customer who reads about it a week later has been told; a customer who watches
 * it happen can pick up the phone while it is happening, and only the second of
 * those is a customer who believes the answer.
 *
 * For the OPERATOR it is a different job: never be in doubt about whose data is
 * on the screen. The console is styled deliberately unlike the app for the same
 * reason, but a visit puts the vendor inside the app itself, wearing the
 * customer's own company name in the corner. This is what stops that being
 * confusing, so it is loud on purpose and cannot be dismissed.
 *
 * WHAT IT SAYS IS WHAT IS ACTUALLY TRUE
 * -------------------------------------
 * `read only` and the money line are not reassurance written by hand — they are
 * the two facts the server enforced when the visit began. `money_visible` comes
 * off the visit row, which is the same row the permission matrix was built
 * from, so the sentence and the rule cannot drift apart.
 */

interface Props {
  readonly visit: SupportVisit;
  /** Called after the visitor leaves, so the shell can send them back. */
  readonly onLeft?: () => void;
}

export function SupportBanner({ visit, onLeft }: Props): JSX.Element {
  const [left, setLeft] = useState(() => minutesLeft(visit.expires_at));
  const [busy, setBusy] = useState(false);

  /*
   * The clock ticks, because a fixed "ends at 14:32" is a number somebody has
   * to subtract from the time to make sense of. Every fifteen seconds is often
   * enough for a thirty-minute visit and cheap enough not to think about.
   */
  useEffect(() => {
    const timer = setInterval(() => setLeft(minutesLeft(visit.expires_at)), 15_000);
    return () => clearInterval(timer);
  }, [visit.expires_at]);

  const remaining =
    left <= 0 ? 'ending now' : left === 1 ? '1 minute left' : `${left} minutes left`;

  if (visit.you_are_visiting) {
    return (
      <div className="support-banner support-banner-visiting" role="status">
        <div className="support-banner-text">
          <strong>You are looking at somebody else’s company.</strong>{' '}
          <span>
            Read only — nothing you do here can change their records, and you cannot download
            their files. {remaining}.
          </span>
          <span className="support-banner-detail">
            {visit.money_visible
              ? 'Their money is visible on this visit, and their audit trail says so.'
              : 'Their money is hidden on this visit.'}{' '}
            You told them: “{visit.reason}”
          </span>
        </div>
        <button
          type="button"
          className="support-banner-leave"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void leaveCompany()
              .then(() => {
                if (onLeft) onLeft();
                else window.location.assign('/operator');
              })
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Leaving…' : 'Leave now'}
        </button>
      </div>
    );
  }

  /*
   * The customer's half. It names the person, says what they can and cannot do,
   * and quotes the reason they gave WITHOUT editing it — the reason was written
   * knowing the customer would read it, and softening it here would make the
   * whole record worth less.
   */
  return (
    <div className="support-banner support-banner-watched" role="status">
      <div className="support-banner-text">
        <strong>{VENDOR} support is looking at your data.</strong>{' '}
        <span>
          {visit.operator_name} can see your projects but cannot change anything, and cannot
          download your files. {remaining}.
        </span>
        <span className="support-banner-detail">
          {visit.money_visible
            ? 'This visit CAN see your costs, budgets, variations and invoices.'
            : 'This visit cannot see your costs, budgets, variations or invoices.'}{' '}
          Their reason: “{visit.reason}” — it is in your audit trail too.
        </span>
      </div>
    </div>
  );
}

/** Whole minutes until the visit ends. Never negative. */
function minutesLeft(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 60_000);
}
